import { buildAskPrompt, buildDocContextPrompt } from './prompts'
import { availablePromptActions } from './getters'
import type { SearchQueryParams } from 'afpnews-api'
import { MAX_TOOL_ITERATIONS } from '@/config/ai/constants'
import { serializeSearchParams } from '@/lib/ai/serializeForAi'

const SKILL_REF_PATTERN = /(?<=^|\s)\/([a-z0-9_-]+)/gi

// Message de la forme `/skill arguments...` (le skill et ses arguments
// constituent tout le message). Les arguments peuvent s'étendre sur plusieurs
// lignes. Sert à passer un argument à un skill (ex. `/briefing automobile`).
const SKILL_COMMAND_PATTERN = /^\s*\/([a-z0-9_-]+)(?:[ \t]+([\s\S]+?))?\s*$/i

// Injecte l'argument utilisateur dans le contenu d'un skill. Si le corps
// contient le marqueur `$ARGUMENTS`, on le substitue ; sinon, on annexe une
// précision en fin de contenu. Sans argument, le contenu est inchangé.
function applySkillArguments(content: string, args: string): string {
  if (content.includes('$ARGUMENTS')) {
    return content.replaceAll('$ARGUMENTS', args)
  }
  if (args) {
    return `${content}\n\nPrécision de l'utilisateur : ${args}`
  }
  return content
}

export function resolveAutoPrompt(
  promptId: string,
  { searchParams, docId }: { searchParams?: SearchQueryParams; docId?: string }
): string | null {
  if (promptId === 'digest' && searchParams) {
    return `/digest ${serializeSearchParams(searchParams)}`.trim()
  }

  if (promptId === 'ask') return buildAskPrompt(docId)

  const skill = availablePromptActions.value.find(item => item.id === promptId)
  if (!skill) return null

  // Skill contextuel déclenché depuis un viewer : injecter le contexte du
  // document avant la référence de skill (expansée à l'envoi par
  // expandSkillRefs). Le LLM dispose ainsi du texte de la dépêche.
  if (skill.type === 'contextual' && docId) {
    const context = buildDocContextPrompt(docId)
    return context ? `${context}\n\n/${promptId}` : `/${promptId}`
  }

  return `/${promptId}`
}

export function maxStepsFor(text: string): number {
  const ids = [...text.matchAll(SKILL_REF_PATTERN)].map(match => match[1])
  return Math.max(
    MAX_TOOL_ITERATIONS,
    ...availablePromptActions.value
      .filter(skill => ids.includes(skill.id))
      .map(skill => skill.maxSteps ?? 0)
  )
}

export function expandSkillRefs(text: string): string {
  // Cas commande : le message entier est `/skill arguments...`. On injecte
  // les arguments dans le contenu du skill (marqueur `$ARGUMENTS`).
  const command = text.match(SKILL_COMMAND_PATTERN)
  if (command) {
    const skill = availablePromptActions.value.find(
      item => item.id === command[1]
    )
    if (skill) {
      const content = skill.buildContent() || `/${command[1]}`
      return applySkillArguments(content, (command[2] ?? '').trim())
    }
  }

  // Sinon : expansion classique des références (sans arguments), qui
  // préserve le texte autour de chaque `/skill`.
  return text.replace(SKILL_REF_PATTERN, (_match, id) => {
    const skill = availablePromptActions.value.find(item => item.id === id)
    if (!skill) return `/${id}`
    return skill.buildContent() || `/${id}`
  })
}
